import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { format, isSameYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/Badge';
import { FavoriteButton } from '@/components/ui/FavoriteButton';
import { ROLE_BADGE_TONE } from '@/components/ui/Badge/tones';
import { PointsValue } from '@/components/ui/PointsValue';
import { RankPill } from '@/components/ui/RankPill';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { TOURNAMENT_ROLES, TOURNAMENT_STATUSES } from '@/constants';
import type { TournamentRole } from '@/constants';
import { getDateLocale } from '@/utils/dateLocale';
import { visibleRank } from '@/utils/tournamentRank';
import type { Tournament } from '@/types/shared';
import styles from './styles.module.scss';

export interface TournamentCardFavorite {
  active: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

interface TournamentCardProps {
  tournament: Tournament;
  onClick?: () => void;
  /**
   * The user's rank, points and role — for lists of the user's own
   * tournaments (dashboard, "my" tab).
   */
  showUserStats?: boolean;
  /**
   * Status badge, season and the status-dependent date line. Off for the
   * compact dashboard slider.
   */
  showDetails?: boolean;
  /** Status badge (with `showDetails`); off where every card shares one status (archive). */
  showStatus?: boolean;
  /**
   * Optional action (e.g. an "Unarchive" button) under the card. A <button>
   * nested inside a <Link> is invalid HTML, so with an action the card is a
   * <div> and the Link covers only the card content.
   */
  action?: React.ReactNode;
  /**
   * Favorite toggle in the top-right corner, in place of the chevron. Like an
   * action it sits outside the Link, so a tap on it never opens the tournament.
   */
  favorite?: TournamentCardFavorite;
  /** Optional muted line under the card (e.g. archive date), next to the action when there is one. */
  meta?: React.ReactNode;
  /** Sizing / positioning from the container (e.g. slider flex-basis). */
  className?: string;
}

const ROLE_KEYS: Record<TournamentRole, string> = {
  [TOURNAMENT_ROLES.HOLDER]: 'tournament:list.role.holder',
  [TOURNAMENT_ROLES.MAINTAINER]: 'tournament:list.role.maintainer',
  [TOURNAMENT_ROLES.PARTICIPANT]: 'tournament:list.role.participant',
};

function formatDay(iso: string, withTime: boolean): string {
  const date = new Date(iso);
  const pattern = isSameYear(date, new Date()) ? 'd MMM' : 'd MMM yyyy';
  return format(date, withTime ? `${pattern}, HH:mm` : pattern, { locale: getDateLocale() });
}

/** Status-dependent muted line: next match / start / creation date (device time zone). */
function getDateLine(tournament: Tournament, t: TFunction): string | null {
  switch (tournament.listStatus) {
    case TOURNAMENT_STATUSES.ACTIVE:
      return tournament.nextMatchDate
        ? t('tournament:list.nextMatch', { date: formatDay(tournament.nextMatchDate, true) })
        : null;
    case TOURNAMENT_STATUSES.UPCOMING:
      return tournament.firstMatchDate
        ? t('tournament:list.startsOn', { date: formatDay(tournament.firstMatchDate, true) })
        : t('tournament:list.noMatchesYet');
    case TOURNAMENT_STATUSES.FINISHED:
      return tournament.createdAt
        ? t('tournament:list.createdOn', { date: formatDay(tournament.createdAt, false) })
        : null;
    default:
      return null;
  }
}

/**
 * Tournament card: logo + name + league, optional status / role badges and
 * date line, then a footer with the user's points and match / participant
 * counts. The user's place sits in the top-left corner once they have points.
 */
const TournamentCard = ({
  tournament,
  onClick,
  showUserStats = false,
  showDetails = true,
  showStatus = true,
  action,
  favorite,
  meta,
  className,
}: TournamentCardProps) => {
  const { t } = useTranslation('tournament');
  const matchesCount = tournament.matchesCount ?? 0;
  const participantsCount = tournament.participantsCount ?? 0;
  const leagueLine = [tournament.leagueName, showDetails ? tournament.season : null]
    .filter(Boolean)
    .join(' · ');
  const dateLine = showDetails ? getDateLine(tournament, t) : null;
  const rank = showUserStats ? visibleRank(tournament.userRank, tournament.userPoints) : null;
  const role = showUserStats && showDetails ? tournament.userRole : undefined;
  const status = showDetails && showStatus ? tournament.listStatus : undefined;
  const hasBadges = Boolean(status || role);

  const content = (
    <>
      {rank !== null && <RankPill rank={rank} className={styles.rank} testId="tournament-card-rank" />}

      <div className={styles.top}>
        {tournament.leagueLogo ? (
          <img
            className={styles.logo}
            src={tournament.leagueLogo}
            alt={tournament.leagueName || ''}
            // Intrinsic size reserves layout space before decode. No
            // `loading="lazy"` — many cards mount in one commit.
            width={42}
            height={42}
            decoding="async"
          />
        ) : (
          <span className={styles.logoPlaceholder} aria-hidden="true" />
        )}
        <div className={styles.identity}>
          <h4 className={styles.name} data-testid="tournament-card-name">{tournament.name}</h4>
          {leagueLine && <span className={styles.league}>{leagueLine}</span>}
          {dateLine && <span className={styles.dateLine}>{dateLine}</span>}
        </div>
        {!action && !favorite && <i className={classNames('icon-chevron-right', styles.chevron)} aria-hidden="true" />}
      </div>

      {hasBadges && (
        <div className={styles.badges}>
          {status && <StatusBadge status={status} variant="soft" />}
          {role && (
            <Badge tone={ROLE_BADGE_TONE[role]} variant="soft" testId="tournament-card-role">
              {t(ROLE_KEYS[role])}
            </Badge>
          )}
        </div>
      )}

      <div className={styles.footer}>
        {showUserStats && <PointsValue points={tournament.userPoints} testId="tournament-card-points" />}
        <div className={styles.stats}>
          <span className={styles.stat}>
            <i className="icon-ball" aria-hidden="true" />
            <b>{matchesCount}</b>
            <span className={styles.statLabel}>
              {t('tournament:list.matchesLabel', { count: matchesCount })}
            </span>
          </span>
          <span className={styles.stat}>
            <i className="icon-users" aria-hidden="true" />
            <b>{participantsCount}</b>
            <span className={styles.statLabel}>
              {t('tournament:list.participantsLabel', { count: participantsCount })}
            </span>
          </span>
        </div>
      </div>
    </>
  );

  const footerRow = (action || meta) && (
    <div className={styles.actionRow}>
      {meta && <span className={styles.meta}>{meta}</span>}
      {action}
    </div>
  );

  if (action || favorite) {
    return (
      <div
        className={classNames(styles.card, styles.withAction, favorite && styles.withFavorite, className)}
        data-testid="tournament-card"
      >
        <Link to={`/tournaments/${tournament.id}`} className={styles.cardLink} onClick={onClick}>
          {content}
        </Link>
        {favorite && (
          <FavoriteButton
            active={favorite.active}
            onToggle={favorite.onToggle}
            disabled={favorite.disabled}
            ariaLabel={t(favorite.active ? 'tournament:favorites.remove' : 'tournament:favorites.add')}
            testId="tournament-favorite-toggle"
            className={styles.favorite}
          />
        )}
        {footerRow}
      </div>
    );
  }

  return (
    <Link
      to={`/tournaments/${tournament.id}`}
      className={classNames(styles.card, className)}
      onClick={onClick}
      data-testid="tournament-card"
    >
      {content}
      {footerRow}
    </Link>
  );
};

export default TournamentCard;
